import { RadioChannel, RadioRegion } from "@somewear/api";
import {
	ButtonFunction,
	InmarsatLeaseType,
	RadioBandwidth,
	RadioMode,
	RadioSpreadFactor,
} from "@somewear/api/src/proto/command_public_proto_pb";
import {
	ButtonFunctionIds,
	enableableField,
	InmarsatLeaseTypeIds,
	MeshKeyMethodVersion,
	optionalBoolSchema,
	protoToZodEnum,
	RadioModeIds,
	TrackingIntervalIds,
} from "@somewear/model";
import { z } from "zod";

// What an unconfigured device actually does: mirrors the firmware factory defaults
// (DEFAULT_SETTINGS in bond src/settings/somewear_config.h). Used as the display
// fallback ONLY for existing workspaces with unset settings, where these values are
// the truth about their devices.
export const SURVIVABLE_BASELINE_SETTINGS = {
	trackingIntervals: {
		mesh: TrackingIntervalIds.ATAK30,
		sat: TrackingIntervalIds.ATAK30,
	},
	lowBandwidthTrackingMultiplier: 1,
	radioChannel: null,
	backhaul: false,
	altitude: false,
	speedAndCourse: false,
	trackingOn: true,
	enableLed: true,
	enableHaptics: true,
	enableSatellite: true,
	enduranceMode: false,
	enableMesh: true,
	buttonFunction: ButtonFunctionIds.TRACKING,
	radioMode: RadioModeIds.ENABLED,
	meshKeyMethodVersion: MeshKeyMethodVersion.UNKNOWN,
	inmarsatFrequencies: {
		rx: 1550,
		tx: 1650,
	},
	inmarsatLeaseType: InmarsatLeaseType.INMARSATLEASETYPELAISR,
};

// The product-recommended defaults from Will S. This is what the backend seeds into every NEW workspace
// wherever the org has not configured a value, should be kept in sync with
// souvla: app/src/main/java/com/somewear/app/device/RecommendedDefaultSettings.kt
// Used as the display fallback for the ORG defaults form and the new-workspace
// dialog, so those screens always show exactly what a new workspace will get.
export const RECOMMENDED_DEFAULT_SETTINGS = {
	...SURVIVABLE_BASELINE_SETTINGS,
	backhaul: true,
	trackingIntervals: {
		mesh: TrackingIntervalIds.ATAK30,
		sat: TrackingIntervalIds.ATAK2Min,
	},
	lowBandwidthTrackingMultiplier: 4,
};

export function getDefaultRadioChannel(radioChannels: RadioChannel.AsObject[]) {
	const initialRadioChannel = radioChannels.find(
		(rc) => rc.region === RadioRegion.RADIOREGIONUS && rc.name === "Americas 1",
	);

	if (!initialRadioChannel) return;
	return {
		enabled: true,
		value: initialRadioChannel.id,
	};
}

export const workspaceDefaultsFieldsSchema = z.object({
	trackingOn: enableableField(z.boolean().default(SURVIVABLE_BASELINE_SETTINGS.trackingOn)),
	backhaul: enableableField(z.boolean().default(SURVIVABLE_BASELINE_SETTINGS.backhaul)),
	enableLed: enableableField(z.boolean().default(SURVIVABLE_BASELINE_SETTINGS.enableLed)),
	enableHaptics: enableableField(z.boolean().default(SURVIVABLE_BASELINE_SETTINGS.enableHaptics)),
	enableSatellite: enableableField(
		z.boolean().default(SURVIVABLE_BASELINE_SETTINGS.enableSatellite),
	),
	enduranceMode: enableableField(z.boolean().default(SURVIVABLE_BASELINE_SETTINGS.enduranceMode)),
	buttonFunction: enableableField(
		z.nativeEnum(ButtonFunctionIds).default(SURVIVABLE_BASELINE_SETTINGS.buttonFunction),
	),
	altitude: enableableField(z.boolean().default(SURVIVABLE_BASELINE_SETTINGS.altitude)),
	speedAndCourse: enableableField(
		z.boolean().default(SURVIVABLE_BASELINE_SETTINGS.speedAndCourse),
	),
	trackingIntervals: enableableField(
		z
			.union([
				z.object({
					mesh: z.nativeEnum(TrackingIntervalIds),
					sat: z.nativeEnum(TrackingIntervalIds),
				}),
				z.nativeEnum(TrackingIntervalIds),
			])
			.default(SURVIVABLE_BASELINE_SETTINGS.trackingIntervals),
	),
	radioChannel: enableableField(z.number().nullable().default(null)),
	radioMode: enableableField(
		z.nativeEnum(RadioModeIds).default(SURVIVABLE_BASELINE_SETTINGS.radioMode),
	),
	meshKeyMethodVersion: enableableField(
		z
			.nativeEnum(MeshKeyMethodVersion)
			.default(SURVIVABLE_BASELINE_SETTINGS.meshKeyMethodVersion),
	),
	inmarsatFrequencies: enableableField(
		z
			.object({
				rx: z.number(),
				tx: z.number(),
			})
			.default(SURVIVABLE_BASELINE_SETTINGS.inmarsatFrequencies),
	),
	inmarsatLeaseType: enableableField(
		z.nativeEnum(InmarsatLeaseTypeIds).default(SURVIVABLE_BASELINE_SETTINGS.inmarsatLeaseType),
	),
});

export const workspaceDefaultsSchema = workspaceDefaultsFieldsSchema.extend({
	workspaceId: z.string(),
});

export const baseWorkspaceDeviceDefaultsRequestSchema = z.object({
	// Optional Bools
	trackingOn: optionalBoolSchema,
	backhaul: optionalBoolSchema,
	altitude: optionalBoolSchema,
	speedAndCourse: optionalBoolSchema,
	ledDisabled: optionalBoolSchema,
	hapticsDisabled: optionalBoolSchema,
	satDisabled: optionalBoolSchema,
	iridiumNoSleep: optionalBoolSchema,
	// Tracking Interval = trackingInterval + gpsInterval
	trackingInterval: z.number(),
	gpsInterval: z.number(),
	lowBandwidthTrackingMultiplier: z.number(),
	// Radio Channel = lowSpeedFrequency + highSpeedFrequency
	lowSpeedFrequency: z.number(),
	highSpeedFrequency: z.number(),
	// Radio Data Rate = bandwidth + spreadFactor
	lowSpeedBandwidth: protoToZodEnum(RadioBandwidth),
	lowSpeedSpreadFactor: protoToZodEnum(RadioSpreadFactor),

	highSpeedBandwidth: protoToZodEnum(RadioBandwidth).nullable(),
	highSpeedSpreadFactor: protoToZodEnum(RadioSpreadFactor).nullable(),

	radioMode: protoToZodEnum(RadioMode), // Maps to "Enable Mesh"
	buttonFunction: protoToZodEnum(ButtonFunction).optional(),
	meshKeyMethodVersion: optionalBoolSchema,
	inmarsatRxFrequencyHz: z.number(),
	inmarsatTxFrequencyHz: z.number(),
	inmarsatLeaseType: protoToZodEnum(InmarsatLeaseType),
});

export const workspaceDefaultsRequestSchema = baseWorkspaceDeviceDefaultsRequestSchema.extend({
	workspaceId: z.string(),
});

export type BaseWorkspaceDefaultsRequestSchema = z.infer<
	typeof baseWorkspaceDeviceDefaultsRequestSchema
>;
export type WorkspaceDefaultsSchema = z.infer<typeof workspaceDefaultsSchema>;
export type WorkspaceDefaultsFieldsSchema = z.infer<typeof workspaceDefaultsFieldsSchema>;
export type WorkspaceDefaultsRequestSchema = z.infer<typeof workspaceDefaultsRequestSchema>;

export function isWorkspaceDeviceDefaultsField(
	key: string,
): key is keyof WorkspaceDefaultsFieldsSchema {
	return key in workspaceDefaultsFieldsSchema.shape;
}
